// Walkable-ground geometry (§18.3): containment tests and nearest-boundary queries for the shapes
// arenas are built from (disc, rounded rectangle, polygon, annulus sector, flat-top hex grid).
// Every query writes into a caller-owned GroundHit; nothing here allocates per call.
import { TAU, closestOnSegment, wrapAngle } from '../math';
import { datan2, dcos, dsin } from '../sim/dmath';
import type { Vec2 } from '../types';
import type { GroundHit } from './mapTypes';

/** Distances below this are treated as "on the boundary" when deriving a normal. */
const ON_EDGE_EPS = 1e-9;
export const SQRT3 = Math.sqrt(3);

const seg: Vec2 = { x: 0, y: 0 };

/** Running minimum over boundary candidates (single-threaded scratch state). */
const best = { d2: Infinity, x: 0, y: 0, nx: 0, ny: 0 };

function resetBest(x: number, y: number): void {
  best.d2 = Infinity;
  best.x = x;
  best.y = y;
  best.nx = 0;
  best.ny = 0;
}

function consider(x: number, y: number, px: number, py: number, nx: number, ny: number): void {
  const d2 = (x - px) ** 2 + (y - py) ** 2;
  if (d2 < best.d2) {
    best.d2 = d2;
    best.x = px;
    best.y = py;
    best.nx = nx;
    best.ny = ny;
  }
}

/**
 * Completes a hit at boundary point (bx, by). The normal points into the region: from the
 * boundary toward the query point when it is inside, the other way when it is outside. Exactly
 * on the boundary the shape's own inward normal (fnx, fny) is used. `dist` is unsigned.
 */
function finishHit(out: GroundHit, x: number, y: number, bx: number, by: number, inside: boolean, fnx: number, fny: number): GroundHit {
  const dx = x - bx;
  const dy = y - by;
  const d = Math.hypot(dx, dy);
  out.x = bx;
  out.y = by;
  out.dist = d;
  if (d > ON_EDGE_EPS) {
    const s = inside ? 1 / d : -1 / d;
    out.nx = dx * s;
    out.ny = dy * s;
  } else {
    out.nx = fnx;
    out.ny = fny;
  }
  return out;
}

function finishBest(out: GroundHit, x: number, y: number, inside: boolean): GroundHit {
  return finishHit(out, x, y, best.x, best.y, inside, best.nx, best.ny);
}

// ---- Disc ---------------------------------------------------------------------------------------

export function inDisc(x: number, y: number, cx: number, cy: number, r: number): boolean {
  const dx = x - cx;
  const dy = y - cy;
  return dx * dx + dy * dy <= r * r;
}

export function nearestOnDisc(x: number, y: number, cx: number, cy: number, r: number, out: GroundHit): GroundHit {
  const dx = x - cx;
  const dy = y - cy;
  const d = Math.hypot(dx, dy);
  const ux = d > ON_EDGE_EPS ? dx / d : 1;
  const uy = d > ON_EDGE_EPS ? dy / d : 0;
  return finishHit(out, x, y, cx + ux * r, cy + uy * r, d <= r, -ux, -uy);
}

// ---- Rounded rectangle --------------------------------------------------------------------------

/** Axis-aligned rectangle centered at (cx, cy) with half extents (hx, hy) and corner radius r. */
export interface RoundedRect { readonly cx: number; readonly cy: number; readonly hx: number; readonly hy: number; readonly r: number }

export function inRoundedRect(x: number, y: number, rr: RoundedRect): boolean {
  const dx = Math.abs(x - rr.cx) - (rr.hx - rr.r);
  const dy = Math.abs(y - rr.cy) - (rr.hy - rr.r);
  if (dx > 0 && dy > 0) return dx * dx + dy * dy <= rr.r * rr.r;
  return dx <= rr.r && dy <= rr.r;
}

export function nearestOnRoundedRect(x: number, y: number, rr: RoundedRect, out: GroundHit): GroundHit {
  const sx = x >= rr.cx ? 1 : -1;
  const sy = y >= rr.cy ? 1 : -1;
  const ix = rr.hx - rr.r;
  const iy = rr.hy - rr.r;
  const dx = Math.abs(x - rr.cx) - ix;
  const dy = Math.abs(y - rr.cy) - iy;
  if (dx > 0 && dy > 0) return nearestOnDisc(x, y, rr.cx + sx * ix, rr.cy + sy * iy, rr.r, out);
  if (dx >= dy) return finishHit(out, x, y, rr.cx + sx * rr.hx, y, dx <= rr.r, -sx, 0);
  return finishHit(out, x, y, x, rr.cy + sy * rr.hy, dy <= rr.r, 0, -sy);
}

// ---- Polygon ------------------------------------------------------------------------------------

/** A simple polygon as flat coordinate arrays; `ccw` caches the winding for inward edge normals. */
export interface Polygon { readonly xs: readonly number[]; readonly ys: readonly number[]; readonly ccw: boolean }

export function createPolygon(points: readonly Vec2[]): Polygon {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  let area2 = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) area2 += xs[j] * ys[i] - xs[i] * ys[j];
  return { xs, ys, ccw: area2 > 0 };
}

/** Even-odd ray cast. */
export function inPolygon(x: number, y: number, poly: Polygon): boolean {
  const { xs, ys } = poly;
  let inside = false;
  for (let i = 0, j = xs.length - 1; i < xs.length; j = i++) {
    if ((ys[i] > y) !== (ys[j] > y) && x < ((xs[j] - xs[i]) * (y - ys[i])) / (ys[j] - ys[i]) + xs[i]) inside = !inside;
  }
  return inside;
}

export function nearestOnPolygon(x: number, y: number, poly: Polygon, out: GroundHit): GroundHit {
  const { xs, ys } = poly;
  resetBest(x, y);
  for (let i = 0, j = xs.length - 1; i < xs.length; j = i++) {
    const ex = xs[i] - xs[j];
    const ey = ys[i] - ys[j];
    const l = Math.hypot(ex, ey) || 1;
    closestOnSegment(x, y, xs[j], ys[j], xs[i], ys[i], seg);
    consider(x, y, seg.x, seg.y, (poly.ccw ? -ey : ey) / l, (poly.ccw ? ex : -ex) / l);
  }
  return finishBest(out, x, y, inPolygon(x, y, poly));
}

// ---- Annulus sector -----------------------------------------------------------------------------

/** Ring slice r ∈ [r0, r1] spanning angles [a0, a0 + span] (radians, counter-clockwise from +x). */
export interface AnnulusSector { readonly cx: number; readonly cy: number; readonly r0: number; readonly r1: number; readonly a0: number; readonly span: number }

function angleOffset(s: AnnulusSector, x: number, y: number): number {
  const off = wrapAngle(datan2(y - s.cy, x - s.cx) - s.a0);
  return off < 0 ? off + TAU : off;
}

export function inAnnulusSector(x: number, y: number, s: AnnulusSector): boolean {
  const d = Math.hypot(x - s.cx, y - s.cy);
  return d >= s.r0 && d <= s.r1 && angleOffset(s, x, y) <= s.span;
}

function considerRadialEdge(x: number, y: number, s: AnnulusSector, angle: number, side: number): void {
  const ux = dcos(angle);
  const uy = dsin(angle);
  closestOnSegment(x, y, s.cx + ux * s.r0, s.cy + uy * s.r0, s.cx + ux * s.r1, s.cy + uy * s.r1, seg);
  consider(x, y, seg.x, seg.y, -uy * side, ux * side);
}

export function nearestOnAnnulusSector(x: number, y: number, s: AnnulusSector, out: GroundHit): GroundHit {
  const off = angleOffset(s, x, y);
  // Outside the span, the nearest arc point is the closer end of the arc.
  const arcOff = off <= s.span ? off : off - s.span < TAU - off ? s.span : 0;
  const ca = dcos(s.a0 + arcOff);
  const sa = dsin(s.a0 + arcOff);
  resetBest(x, y);
  if (s.r0 > 0) consider(x, y, s.cx + ca * s.r0, s.cy + sa * s.r0, ca, sa);
  consider(x, y, s.cx + ca * s.r1, s.cy + sa * s.r1, -ca, -sa);
  if (s.span < TAU) {
    considerRadialEdge(x, y, s, s.a0, 1);
    considerRadialEdge(x, y, s, s.a0 + s.span, -1);
  }
  return finishBest(out, x, y, inAnnulusSector(x, y, s));
}

// ---- Flat-top hex grid --------------------------------------------------------------------------

/** Axial neighbor offsets in edge order: edge k joins vertices k and k+1 (vertex k at 60k°). */
export const HEX_DIRS: readonly (readonly [number, number])[] = [[1, 0], [0, 1], [-1, 1], [-1, 0], [0, -1], [1, -1]];

export interface HexCoord { q: number; r: number }

/** Cube rounding of fractional axial coordinates. */
export function hexRound(fq: number, fr: number, out: HexCoord): HexCoord {
  const fs = -fq - fr;
  let q = Math.round(fq);
  let r = Math.round(fr);
  const s = Math.round(fs);
  const dq = Math.abs(q - fq);
  const dr = Math.abs(r - fr);
  const ds = Math.abs(s - fs);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  out.q = q;
  out.r = r;
  return out;
}

/** Hex distance from the origin in axial coordinates (the ring index). */
export function hexRing(q: number, r: number): number {
  return Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
}

/**
 * Point in a flat-top hex cell centered at (cx, cy) with circumradius R and flat-to-flat height
 * `flat` (√3·R for a regular hexagon).
 */
export function inHex(x: number, y: number, cx: number, cy: number, R: number, flat: number = SQRT3 * R): boolean {
  const ax = Math.abs(x - cx);
  const ay = Math.abs(y - cy);
  return ay <= flat / 2 && ax + (ay * R) / flat <= R;
}

/**
 * A hexagonal patch of flat-top hexes out to `rings`, with circumradius R and row spacing `flat`
 * (flat-to-flat): tile centers at x = 1.5·R·q, y = flat·(r + q/2). Tiles are indexed q-major
 * (q ascending, then r ascending); `neighbors[i*6+k]` is the tile across edge k, or −1. Cell
 * vertices are (R·cos θ, flat/√3 · sin θ) at θ = 60k°, which are exactly the cells `hexTileAt`
 * rounds to even when `flat` is not √3·R.
 */
export interface HexGrid {
  readonly R: number;
  readonly flat: number;
  readonly rings: number;
  readonly count: number;
  readonly q: readonly number[];
  readonly r: readonly number[];
  readonly cx: readonly number[];
  readonly cy: readonly number[];
  readonly neighbors: readonly number[];
  /** Edge endpoints, 4 numbers (x0, y0, x1, y1) per tile edge, at `(i*6+k)*4`. */
  readonly edges: readonly number[];
  /** Inward unit normal of edge k (the same for every tile), at `k*2`. */
  readonly edgeNormals: readonly number[];
}

/** q-major index of axial (q, r) in a patch of `rings` rings, or −1 outside it. */
function axialIndex(rings: number, q: number, r: number): number {
  if (hexRing(q, r) > rings) return -1;
  let i = 0;
  for (let qi = -rings; qi < q; qi++) i += 2 * rings + 1 - Math.abs(qi);
  return i + r - Math.max(-rings, -q - rings);
}

export function createHexGrid(R: number, flat: number, rings: number): HexGrid {
  const q: number[] = [];
  const r: number[] = [];
  for (let qi = -rings; qi <= rings; qi++) {
    for (let ri = Math.max(-rings, -qi - rings); ri <= Math.min(rings, -qi + rings); ri++) {
      q.push(qi);
      r.push(ri);
    }
  }
  const count = q.length;
  const cx = q.map((qi) => 1.5 * R * qi);
  const cy = q.map((qi, i) => flat * (r[i] + qi / 2));
  const vx: number[] = [];
  const vy: number[] = [];
  for (let k = 0; k < 6; k++) {
    vx.push(R * dcos((k * Math.PI) / 3));
    vy.push((flat / SQRT3) * dsin((k * Math.PI) / 3));
  }
  const edgeNormals: number[] = [];
  for (let k = 0; k < 6; k++) {
    const ex = vx[(k + 1) % 6] - vx[k];
    const ey = vy[(k + 1) % 6] - vy[k];
    const l = Math.hypot(ex, ey);
    // Vertices run counter-clockwise, so the left normal points into the cell.
    edgeNormals.push(-ey / l, ex / l);
  }
  const neighbors: number[] = [];
  const edges: number[] = [];
  for (let i = 0; i < count; i++) {
    for (let k = 0; k < 6; k++) {
      neighbors.push(axialIndex(rings, q[i] + HEX_DIRS[k][0], r[i] + HEX_DIRS[k][1]));
      const k1 = (k + 1) % 6;
      edges.push(cx[i] + vx[k], cy[i] + vy[k], cx[i] + vx[k1], cy[i] + vy[k1]);
    }
  }
  return { R, flat, rings, count, q, r, cx, cy, neighbors, edges, edgeNormals };
}

/** Index of tile (q, r), or −1 when it is outside the patch. */
export function hexIndex(grid: HexGrid, q: number, r: number): number {
  return axialIndex(grid.rings, q, r);
}

const hexScratch: HexCoord = { q: 0, r: 0 };

/** Index of the tile whose cell contains (x, y), or −1 outside the patch. */
export function hexTileAt(grid: HexGrid, x: number, y: number): number {
  const fq = x / (1.5 * grid.R);
  hexRound(fq, y / grid.flat - fq / 2, hexScratch);
  return axialIndex(grid.rings, hexScratch.q, hexScratch.r);
}

/**
 * Nearest point on the boundary of the union of walkable tiles (`isWalkable(states[i])`): the
 * edges between a walkable tile and a non-walkable or missing neighbor.
 */
export function nearestOnHexGrid(
  grid: HexGrid, states: readonly number[], isWalkable: (state: number) => boolean,
  x: number, y: number, out: GroundHit,
): GroundHit {
  resetBest(x, y);
  for (let i = 0; i < grid.count; i++) {
    if (!isWalkable(states[i])) continue;
    for (let k = 0; k < 6; k++) {
      const n = grid.neighbors[i * 6 + k];
      if (n >= 0 && isWalkable(states[n])) continue;
      const e = (i * 6 + k) * 4;
      closestOnSegment(x, y, grid.edges[e], grid.edges[e + 1], grid.edges[e + 2], grid.edges[e + 3], seg);
      consider(x, y, seg.x, seg.y, grid.edgeNormals[k * 2], grid.edgeNormals[k * 2 + 1]);
    }
  }
  const tile = hexTileAt(grid, x, y);
  return finishBest(out, x, y, tile >= 0 && isWalkable(states[tile]));
}

// ---- Disc with removable rim sectors (Tipsy Teacup rim chips) ------------------------------------

/**
 * A disc of radius `outer` whose annulus `inner..outer` is split into `count` equal sectors, sector
 * k centered at angle k·(2π/count). Sectors whose bit is set in `removed` are void; the inner disc
 * always remains.
 */
export interface SectoredDisc { readonly inner: number; readonly outer: number; readonly count: number }

/** Index of the sector containing angle `a` (radians). */
export function sectorIndex(d: SectoredDisc, a: number): number {
  const step = TAU / d.count;
  const k = Math.round(a / step) % d.count;
  return k < 0 ? k + d.count : k;
}

export function inSectoredDisc(x: number, y: number, d: SectoredDisc, removed: number): boolean {
  const r2 = x * x + y * y;
  if (r2 <= d.inner * d.inner) return true;
  if (r2 > d.outer * d.outer) return false;
  return (removed & (1 << sectorIndex(d, datan2(y, x)))) === 0;
}

/** Closest point to angle `a` on the arc of radius r spanning [a0, a0 + span]; considered with inward normal −r̂. */
function considerArc(x: number, y: number, r: number, a0: number, span: number, a: number): void {
  let off = wrapAngle(a - a0);
  if (off < 0) off += TAU;
  const t = off <= span ? off : off - span < TAU - off ? span : 0;
  const c = dcos(a0 + t);
  const s = dsin(a0 + t);
  consider(x, y, c * r, s * r, -c, -s);
}

/**
 * Nearest point on the boundary of the remaining ground (inner disc ∪ attached sectors), with the
 * inward normal and unsigned distance, like the other `nearestOn*` queries.
 */
export function nearestOnSectoredDisc(x: number, y: number, d: SectoredDisc, removed: number, out: GroundHit): GroundHit {
  const full = (1 << d.count) - 1;
  if ((removed & full) === 0) return nearestOnDisc(x, y, 0, 0, d.outer, out);
  if ((removed & full) === full) return nearestOnDisc(x, y, 0, 0, d.inner, out);
  const step = TAU / d.count;
  const a = datan2(y, x);
  resetBest(x, y);
  for (let k = 0; k < d.count; k++) {
    const gone = (removed & (1 << k)) !== 0;
    const a0 = (k - 0.5) * step;
    considerArc(x, y, gone ? d.inner : d.outer, a0, step, a);
    // Radial edge on the counter-clockwise side of sector k, where it meets sector k + 1.
    const nextGone = (removed & (1 << ((k + 1) % d.count))) !== 0;
    if (gone === nextGone) continue;
    const ea = (k + 0.5) * step;
    const ux = dcos(ea);
    const uy = dsin(ea);
    closestOnSegment(x, y, ux * d.inner, uy * d.inner, ux * d.outer, uy * d.outer, seg);
    // The normal points into whichever side still has ground.
    const side = gone ? 1 : -1;
    consider(x, y, seg.x, seg.y, -uy * side, ux * side);
  }
  return finishBest(out, x, y, inSectoredDisc(x, y, d, removed));
}
